'use client';

import { Box } from '@nerdwallet/currency';
import Image from '~/app/components/image/image';
import get from 'lodash/get';
import P from 'prop-types';
import { useEffect, useRef } from 'react';

import classNames from 'classnames';
import { usePathname } from 'next/navigation';
import { useMediaQuery } from '~/app/lib/use-media-query';
import UniversalContainer, {
  universalContainerPropTypes,
} from '../universal-container';
import UniversalText from '../universal-text/universal-text';
import styles from './third-party-embed.module.scss';

import {
  group,
  stringInputSpec,
  stringSelectSpec,
  stringTextAreaSpec,
} from '../../lib/editing-configs';
import BaseThirdPartyEmbed from './base-third-party-embed';

const getAlignment = (alignment) => {
  const type = {
    left: 'flex-start',
    center: 'center',
    right: 'flex-end',
  };
  return type[alignment];
};

const ThirdPartyEmbed = ({
  src,
  scriptSrc,
  embedType,
  cffId,
  height,
  width,
  cardIcon,
  title,
  subtitle,
  alignment,
  container,
  cardContainer,
  heightMobile,
  customCss,
}) => {
  const contentRef = useRef();
  const iframe = contentRef.current?.querySelector('iframe');
  const intervalRef = useRef();
  const iframeRef = useRef();
  const pathname = usePathname();

  const size = useMediaQuery();
  const heightValue = size === 'sm' ? heightMobile : height;

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      iframeRef.current = contentRef?.current?.querySelector('iframe');
      try {
        if (iframeRef.current && iframeRef.current?.contentDocument) {
          iframeRef.current.contentDocument.head.innerHTML += `<style id="injected-style" >${customCss}</style>`;
          const cssInjector =
            iframe.contentDocument.getElementById('injected-style');
          if (cssInjector) {
            clearInterval(intervalRef.current);
          }
        }
      } catch (error) {
        console.info('Iframe element not found!');
      }
    }, 500);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [customCss, iframe?.contentDocument]);

  const isCalculatorEmbedType = embedType === 'calculator';

  return (
    <UniversalContainer
      {...container}
      style={{ width: isCalculatorEmbedType ? '100%' : 'auto' }}
    >
      <Box
        className={styles.container}
        style={{
          width: isCalculatorEmbedType ? '100%' : 'auto',
          alignItems: getAlignment(alignment),
        }}
      >
        <UniversalContainer
          {...cardContainer}
          style={{ width: isCalculatorEmbedType ? '100%' : 'auto' }}
        >
          <div
            className={classNames([
              styles.card,
              {
                [styles.calculatorFixWidth]: isCalculatorEmbedType,
              },
            ])}
            ref={contentRef}
            id="content-wrapper"
          >
            {cardIcon && (
              <Image
                objectFit="contain"
                style={{ maxHeight: 90, width: 'auto' }}
                src={cardIcon}
                alt="NerdWallet Brand"
                backgroundColor="transparent"
                naturalHeight={90}
                naturalWidth={221}
                loading="lazy"
                fetchPriority="low"
              />
            )}
            <Box className={styles.cardHeading}>
              {title && <UniversalText {...title} />}
              {subtitle && (
                <UniversalText
                  {...subtitle}
                  defaultOptions={{ text: { textType: 'text' } }}
                />
              )}
            </Box>
            <BaseThirdPartyEmbed
              embedType={embedType}
              iframeSrc={src}
              scriptSrc={scriptSrc}
              cffId={cffId}
              height={heightValue}
              width={width}
              pathname={pathname}
            />
          </div>
        </UniversalContainer>
      </Box>
    </UniversalContainer>
  );
};

ThirdPartyEmbed.propTypes = {
  embedType: P.oneOf(['script', 'iframe']),
  alignment: P.oneOf(['left', 'center', 'right']),
  cardIcon: P.string,
  container: universalContainerPropTypes,
  cardContainer: universalContainerPropTypes,
  title: UniversalText.propTypes,
  subtitle: UniversalText.propTypes,
  children: P.node,
  src: P.string,
  scriptSrc: P.string,
  cffId: P.string,
  formTitle: P.string,
  height: P.string,
  width: P.string,
  heightMobile: P.string,
  disableTopBottomCardColor: P.bool,
  customCss: P.string,
};

ThirdPartyEmbed.defaultProps = {
  src: '',
  scriptSrc: '',
  cffId: '',
  formTitle: '',
  embedType: 'iframe',
  height: '100%',
  width: '100%',
  heightMobile: '',
  disableTopBottomCardColor: false,
};

ThirdPartyEmbed.getEditingConfig = (componentDocument, fieldName = '') => {
  const embedType = fieldName
    ? get(componentDocument.props, `${fieldName}.embedType`, 'iframe')
    : componentDocument?.props?.embedType || 'iframe';

  const getInputName = (name) => {
    if (fieldName) {
      return `${fieldName}.${name}`;
    }
    return name;
  };

  const componentLabel = fieldName
    ? 'Third Party Embed in Grid'
    : 'Third Party Embed';

  const inputConfigMap = {
    script: [
      stringTextAreaSpec(getInputName('scriptSrc'), 'Script Source URL'),
    ],
    iframe: [
      stringInputSpec(getInputName('src'), 'Iframe Source URL'),
      stringInputSpec('height', 'Iframe Height', {
        help: 'Defaults to "100%"',
      }),
      stringInputSpec(getInputName('heightMobile'), 'Iframe Height Mobile'),
      stringInputSpec(getInputName('width'), 'Iframe Width', {
        help: 'Defaults to "100%"',
      }),
      stringInputSpec(getInputName('widthMobile'), 'Iframe Width Mobile'),
    ],
    calculator: [
      stringInputSpec(getInputName('cffId'), 'Calculated Fields Form ID', {
        help: 'For a list of CFFs you can display on landing pages, login to WordPress and navigate to Settings > Calculated Fields Form.',
      }),
    ],
  };

  const embedTypeInputConfig = inputConfigMap[embedType];

  return [
    ...UniversalContainer.getEditingConfig(
      componentDocument,
      'Container',
      [
        'backgroundColor',
        'backgroundImage',
        'border',
        'margin',
        'padding',
        'hide',
        'jumpLinkId',
      ],
      'container'
    ),
    group('Card Container', [
      stringInputSpec(getInputName('cardIcon'), 'Card Icon'),
      ...UniversalContainer.getEditingConfig(
        componentDocument,
        'Card Container',
        [
          'border',
          'margin',
          'padding',
          'backgroundColor',
          'backgroundImage',
          'width',
        ],
        'cardContainer',
        { disableGroup: true }
      ),
    ]),
    ...UniversalText.getEditingConfig(
      componentDocument,
      'Title',
      [
        'fontWeight',
        'groupButton',
        'margin',
        'padding',
        'fontFamily',
        'textColor',
        'textSize',
        'textType',
      ],
      getInputName('title')
    ),
    ...UniversalText.getEditingConfig(
      componentDocument,
      'Subtitle',
      [
        'fontWeight',
        'groupButton',
        'margin',
        'padding',
        'fontFamily',
        'textColor',
        'textSize',
        'textType',
      ],
      getInputName('subtitle'),
      { text: { textType: 'text' } }
    ),

    group(componentLabel, [
      stringSelectSpec(
        getInputName('embedType'),
        'Embed Type',
        [
          { label: 'Iframe', value: 'iframe' },
          { label: 'Script', value: 'script' },
          { label: 'Calculator', value: 'calculator' },
        ],
        {
          help: 'Defaults to "Iframe"',
        }
      ),
      stringSelectSpec(
        getInputName('alignment'),
        'Content Alignment',
        [
          { label: 'Left', value: 'left' },
          { label: 'Center', value: 'center' },
          { label: 'Right', value: 'right' },
        ],
        {
          help: 'Defaults to "Center"',
        }
      ),
      ...embedTypeInputConfig,
      stringTextAreaSpec(getInputName('customCss'), 'Custom CSS'),
    ]),
  ];
};

ThirdPartyEmbed.displayName = 'ThirdPartyEmbed';

export default ThirdPartyEmbed;
